import { useCallback, useState, useRef } from "react";
import * as THREE from "three";

const createCustomPlaneHelper = (plane, position, size) => {
  const geometry = new THREE.PlaneGeometry(size, size);
  const material = new THREE.MeshBasicMaterial({
    color: 0xffff00,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 0.4,
    depthWrite: false,
  });
  const mesh = new THREE.Mesh(geometry, material);

  // The default normal for PlaneGeometry is (0,0,1).
  // Compute the quaternion to rotate from (0,0,1) to our plane's normal.
  const defaultNormal = new THREE.Vector3(0, 0, 1);
  const quaternion = new THREE.Quaternion().setFromUnitVectors(
    defaultNormal,
    plane.normal.clone().normalize()
  );
  mesh.setRotationFromQuaternion(quaternion);
  // Set the position of the helper to the computed position.
  mesh.position.copy(position);

  return mesh;
};

export const useCenterline = (
  stlURLs,
  setProcessing,
  sceneRef,
  modelRef,
  cameraRef,
  containerRef,
  controlsRef,
  viewportRef = null
) => {
  const [resultData, setResultData] = useState(null);
  const [selectedPoints, setSelectedPoints] = useState([]);
  const [selectedPointMeshes, setSelectedPointMeshes] = useState([]);
  const [shortestPath, setShortestPath] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const autoMoveIntervalRef = useRef(null);
  const [pathSliderValue, setPathSliderValue] = useState(0);
  // Ref to store the helper plane so we can update/remove it.
  const planeHelperRef = useRef(null);
  const [crossSectionPoints, setCrossSectionPoints] = useState([]);
  const [isCenterlineMode, setIsCenterlineMode] = useState(false);
  const [isCenterlineMeshSelectMode, setIsCenterlineMeshSelectMode] =
    useState(false);

  const clearCenterline = useCallback(() => {
    if (sceneRef.current) {
      const existingCenterline = sceneRef.current.getObjectByName("centerline");
      if (existingCenterline) {
        sceneRef.current.remove(existingCenterline);
        existingCenterline.geometry.dispose();
        existingCenterline.material.dispose();
        console.log("Previous centerline removed.");
      }
    }
  }, [sceneRef]);

  const clearPreviousPath = useCallback(() => {
    const existingPath = sceneRef.current.getObjectByName("shortestPath");
    if (existingPath) {
      sceneRef.current.remove(existingPath);
      existingPath.geometry.dispose();
      existingPath.material.dispose();
      console.log("Previous path removed.");
    }
  }, [sceneRef]);

  const drawPath = useCallback(
    (pathPoints) => {
      console.log("Drawing path with", pathPoints.length, "points.");
      // Create a new array with additional points between each consecutive pair.
      const quadrupledPoints = [];
      for (let i = 0; i < pathPoints.length - 1; i++) {
        const start = pathPoints[i];
        const end = pathPoints[i + 1];

        // Push the starting point
        quadrupledPoints.push(start);

        // Calculate and insert three equally spaced points between start and end.
        const point1 = new THREE.Vector3().lerpVectors(start, end, 0.25);
        const point2 = new THREE.Vector3().lerpVectors(start, end, 0.5);
        const point3 = new THREE.Vector3().lerpVectors(start, end, 0.75);

        quadrupledPoints.push(point1, point2, point3);
      }
      // Push the last original point.
      quadrupledPoints.push(pathPoints[pathPoints.length - 1]);

      // Create the geometry and material for the line using the new points.
      const geometry = new THREE.BufferGeometry().setFromPoints(
        quadrupledPoints
      );
      const material = new THREE.LineBasicMaterial({
        color: 0x0000ff,
        linewidth: 3,
      });
      const line = new THREE.Line(geometry, material);
      line.name = "shortestPath";
      sceneRef.current.add(line);

      // Update the state with the new, denser set of points.
      setShortestPath(quadrupledPoints);
    },
    [sceneRef, setShortestPath]
  );

  const drawCenterline = useCallback(
    (data) => {
      if (!sceneRef.current || !data || !data.points || !data.edges) {
        console.error("Invalid data or scene is not ready.");
        return;
      }

      const points = data.points.map(([x, y, z]) => new THREE.Vector3(x, y, z));
      const geometry = new THREE.BufferGeometry();
      const vertices = [];

      data.edges.forEach(([startIdx, endIdx]) => {
        const start = points[startIdx];
        const end = points[endIdx];
        vertices.push(start.x, start.y, start.z, end.x, end.y, end.z);
      });

      geometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(vertices, 3)
      );

      const material = new THREE.LineBasicMaterial({
        color: 0xff0000,
        linewidth: 2,
      });

      const lineSegments = new THREE.LineSegments(geometry, material);
      lineSegments.name = "centerline";

      sceneRef.current.add(lineSegments);
    },
    [sceneRef]
  );

  const extractCenterline = useCallback(
    async (stlName) => {
      const matchedSTL = stlURLs.find((stl) => stl.name === stlName);

      if (!matchedSTL) {
        console.error(`STL with name '${stlName}' not found.`);
        return;
      }
      clearCenterline();
      clearPreviousPath();
      setProcessing(true);
      try {
        const response = await fetch(
          "https://centerline-179181786426.europe-west1.run.app/process-stl",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              url: matchedSTL.url,
              storageName: matchedSTL.storageName,
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`Server error: ${response.statusText}`);
        }

        const jsonResponse = await response.json();
        setResultData(jsonResponse);
        drawCenterline(jsonResponse);
      } catch (error) {
        console.error("Error processing STL with server:", error);
        setResultData(null);
      } finally {
        setProcessing(false);
      }
    },
    [stlURLs, setProcessing, clearCenterline, drawCenterline, clearPreviousPath]
  );

  const clearSelectedPoints = useCallback(() => {
    selectedPointMeshes.forEach((mesh) => {
      sceneRef.current.remove(mesh);
      mesh.geometry.dispose();
      mesh.material.dispose();
    });
    setSelectedPointMeshes([]);
    setSelectedPoints([]);
  }, [sceneRef, selectedPointMeshes]);

  const findClosestPointIndex = useCallback((point, centerlinePoints) => {
    let minDistance = Infinity;
    let closestIndex = -1;

    centerlinePoints.forEach((centerlinePoint, index) => {
      const distance = point.distanceTo(centerlinePoint);
      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }, []);

  const findShortestPath = useCallback(
    (startPoint, endPoint) => {
      if (!resultData || !resultData.points || !resultData.edges) {
        console.error("Centerline data is not available.");
        return;
      }

      clearPreviousPath();

      const centerlinePoints = resultData.points.map(
        ([x, y, z]) => new THREE.Vector3(x, y, z)
      );
      const edges = resultData.edges;

      const startIdx = findClosestPointIndex(startPoint, centerlinePoints);
      const endIdx = findClosestPointIndex(endPoint, centerlinePoints);

      if (startIdx === -1 || endIdx === -1) {
        console.error("Unable to find the closest points on the centerline.");
        return;
      }

      const adjacencyList = edges.reduce((acc, [u, v]) => {
        acc[u] = acc[u] || [];
        acc[v] = acc[v] || [];
        acc[u].push(v);
        acc[v].push(u);
        return acc;
      }, {});

      const queue = [[startIdx]];
      const visited = new Set([startIdx]);

      while (queue.length > 0) {
        const path = queue.shift();
        const node = path[path.length - 1];

        if (node === endIdx) {
          const pathPoints = path.map((idx) => centerlinePoints[idx]);
          drawPath(pathPoints);
          return;
        }

        (adjacencyList[node] || []).forEach((neighbor) => {
          if (!visited.has(neighbor)) {
            visited.add(neighbor);
            queue.push([...path, neighbor]);
          }
        });
      }

      console.warn("No path found between the selected points.");
    },
    [resultData, clearPreviousPath, drawPath, findClosestPointIndex]
  );

  const selectTwoPoints = useCallback(() => {
    if (selectedPoints.length > 0) {
      clearSelectedPoints();
      clearPreviousPath();
      return;
    }

    const handleMouseClick = (event) => {
      const rect = containerRef.current.getBoundingClientRect();
      
      // Use viewport dimensions if available, otherwise use container dimensions
      const viewport = viewportRef?.current || {
        left: 0,
        top: 0,
        width: rect.width,
        height: rect.height,
      };
      
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;
      
      const mouse = new THREE.Vector2(
        ((mouseX - viewport.left) / viewport.width) * 2 - 1,
        -((mouseY - viewport.top) / viewport.height) * 2 + 1
      );

      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(mouse, cameraRef.current);

      const visibleModels = modelRef.current.children.filter(
        (child) => child.visible
      );

      if (visibleModels.length > 0) {
        const intersects = raycaster.intersectObjects(visibleModels, true);

        if (intersects.length > 0) {
          const point = intersects[0].point;
          const sphere = new THREE.Mesh(
            new THREE.SphereGeometry(1, 16, 16),
            new THREE.MeshBasicMaterial({ color: 0x00ff00 })
          );

          sphere.position.copy(point);
          sceneRef.current.add(sphere);

          setSelectedPointMeshes((prev) => [...prev, sphere]);
          setSelectedPoints((prevPoints) => {
            const newPoints = [...prevPoints, point];

            if (newPoints.length === 2) {
              containerRef.current.removeEventListener(
                "click",
                handleMouseClick
              );
              findShortestPath(newPoints[0], newPoints[1]);
            }

            return newPoints;
          });
        }
      }
    };

    containerRef.current.removeEventListener("click", handleMouseClick);
    containerRef.current.addEventListener("click", handleMouseClick);
  }, [
    modelRef,
    cameraRef,
    containerRef,
    viewportRef,
    selectedPoints,
    clearSelectedPoints,
    sceneRef,
    clearPreviousPath,
    findShortestPath,
  ]);

  const moveCameraToIndex = useCallback(
    (index) => {
      if (shortestPath.length < 2 || index >= shortestPath.length - 1) {
        console.warn("Invalid index for camera movement.");
        return;
      }

      const currentPoint = shortestPath[index];
      const nextPoint = shortestPath[index + 1];

      // Move the camera to the current point
      cameraRef.current.position.set(
        currentPoint.x,
        currentPoint.y,
        currentPoint.z
      );

      // Look further ahead to prevent sharp direction changes
      const lookAheadPoint =
        index + 3 < shortestPath.length ? shortestPath[index + 3] : nextPoint;

      // Calculate the direction vector from current to lookAheadPoint
      const direction = new THREE.Vector3()
        .subVectors(lookAheadPoint, currentPoint)
        .normalize();

      // Update the camera target (center) dynamically
      const newTarget = new THREE.Vector3().addVectors(currentPoint, direction);
      controlsRef.current.target.copy(newTarget); // Smooth camera orientation

      // Define an 'up' vector (adjust if needed depending on model orientation)
      const up = new THREE.Vector3(0, 1, 0);

      // Create quaternion for smooth rotation
      const quaternion = new THREE.Quaternion().setFromUnitVectors(
        up,
        direction
      );

      // Apply smooth interpolation (slerp) for smooth rotation
      cameraRef.current.quaternion.slerp(quaternion, 0.1); // Adjust 0.1 for smoother/faster rotation

      // Ensure the camera faces towards the new target
      cameraRef.current.lookAt(newTarget);

      // Optional: Ensure the controls are updated after movement
      controlsRef.current.update();
    },
    [shortestPath, cameraRef, controlsRef]
  );

  const autoMoveCamera = useCallback(() => {
    if (shortestPath.length < 2) return;

    // Clear any existing interval to avoid duplicates
    if (autoMoveIntervalRef.current) {
      clearInterval(autoMoveIntervalRef.current);
    }

    autoMoveIntervalRef.current = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        if (prevIndex < shortestPath.length - 1) {
          const newIndex = prevIndex + 1;
          moveCameraToIndex(newIndex);
          return newIndex;
        } else {
          clearInterval(autoMoveIntervalRef.current);
          autoMoveIntervalRef.current = null;
          return prevIndex;
        }
      });
    }, 100); // Adjust the interval (ms) as needed for speed
  }, [shortestPath, moveCameraToIndex]);

  // Function to stop the automatic camera movement.
  const stopAutoMoveCamera = useCallback(() => {
    if (autoMoveIntervalRef.current) {
      clearInterval(autoMoveIntervalRef.current);
      autoMoveIntervalRef.current = null;
    }
  }, []);

  // --- Helper: Slice a geometry with a plane, returning an array of line segments ([v1, v2]) ---
  const sliceMeshWithPlane = useCallback((geometry, plane, matrixWorld) => {
    const posAttr = geometry.getAttribute("position");
    if (!posAttr) {
      console.warn("Geometry missing position attribute.");
      return [];
    }

    // If geometry is indexed, use its indices; otherwise, generate indices for all vertices.
    let indices;
    if (geometry.getIndex()) {
      indices = geometry.getIndex().array;
    } else {
      indices = new Uint32Array(posAttr.count);
      for (let i = 0; i < posAttr.count; i++) {
        indices[i] = i;
      }
    }

    const segments = [];
    const positions = posAttr.array;
    // Create temporary vectors for triangle vertices.
    const vA = new THREE.Vector3();
    const vB = new THREE.Vector3();
    const vC = new THREE.Vector3();

    const checkEdge = (p1, d1, p2, d2, target) => {
      const t = d1 / (d1 - d2);
      target.copy(p2).sub(p1).multiplyScalar(t).add(p1);
    };

    // Iterate over each triangle.
    for (let i = 0; i < indices.length; i += 3) {
      const iA = indices[i],
        iB = indices[i + 1],
        iC = indices[i + 2];

      vA.fromArray(positions, iA * 3);
      vB.fromArray(positions, iB * 3);
      vC.fromArray(positions, iC * 3);

      if (matrixWorld) {
        vA.applyMatrix4(matrixWorld);
        vB.applyMatrix4(matrixWorld);
        vC.applyMatrix4(matrixWorld);
      }

      const dA = plane.distanceToPoint(vA);
      const dB = plane.distanceToPoint(vB);
      const dC = plane.distanceToPoint(vC);

      // If all vertices lie on one side of the plane, skip this triangle.
      if ((dA >= 0 && dB >= 0 && dC >= 0) || (dA <= 0 && dB <= 0 && dC <= 0)) {
        continue;
      }

      const points = [];
      const ds = [dA, dB, dC];
      const vs = [vA, vB, vC];
      const edges = [
        [0, 1],
        [1, 2],
        [2, 0],
      ];

      for (const [e1, e2] of edges) {
        const d1 = ds[e1],
          d2 = ds[e2];
        const p1 = vs[e1],
          p2 = vs[e2];
        if (d1 === 0) {
          points.push(p1.clone());
        } else if (d2 === 0) {
          points.push(p2.clone());
        } else if ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) {
          const inter = new THREE.Vector3();
          checkEdge(p1, d1, p2, d2, inter);
          points.push(inter);
        }
      }

      if (points.length === 2) {
        segments.push([points[0], points[1]]);
      }
    }

    return segments;
  }, []);

  // --- Helper: Transform points into the plane's 2D local space ---
  const transformPointsToPlaneSpace = useCallback(
    (points, planeCenter, planeNormal) => {
      const translated = points.map((p) => p.clone().sub(planeCenter));
      const normal = planeNormal.clone().normalize();

      let u = new THREE.Vector3();
      let v = new THREE.Vector3();

      if (Math.abs(normal.x) < 0.9) {
        u.set(1, 0, 0);
      } else {
        u.set(0, 1, 0);
      }

      v.crossVectors(normal, u).normalize();
      u.crossVectors(v, normal).normalize();

      return translated.map((p) => {
        const x = p.dot(u);
        const y = p.dot(v);
        // Z=0 ile 3D Vector3 döndür
        return new THREE.Vector3(x, y, 0);
      });
    },
    []
  );

  const getCrossSectionWithClustering = useCallback(
    (meshName, pointIndex, maxRadius = null, clusterThreshold = 1.5) => {
      const targetMesh = modelRef.current.children.find(
        (child) => child.name === meshName
      );
      if (!targetMesh || !targetMesh.geometry) return [];

      const totalPoints = shortestPath.length;
      if (totalPoints < 2) return [];

      const clampedIndex = Math.max(0, Math.min(pointIndex, totalPoints - 1));
      const planeCenter = shortestPath[clampedIndex];

      let planeNormal;
      if (clampedIndex < totalPoints - 1) {
        const nextPoint = shortestPath[clampedIndex + 1];
        planeNormal = new THREE.Vector3()
          .subVectors(nextPoint, planeCenter)
          .normalize();
      } else if (clampedIndex > 0) {
        const prevPoint = shortestPath[clampedIndex - 1];
        planeNormal = new THREE.Vector3()
          .subVectors(planeCenter, prevPoint)
          .normalize();
      } else {
        planeNormal = new THREE.Vector3(0, 0, 1);
      }

      const plane = new THREE.Plane(planeNormal, -planeNormal.dot(planeCenter));
      const segments = sliceMeshWithPlane(
        targetMesh.geometry,
        plane,
        targetMesh.matrixWorld
      );

      let allPoints = [];
      segments.forEach((segment) => {
        allPoints.push(segment[0], segment[1]);
      });

      if (allPoints.length === 0) return [];

      allPoints.sort(
        (a, b) => a.distanceTo(planeCenter) - b.distanceTo(planeCenter)
      );

      const centerDistance = allPoints[0].distanceTo(planeCenter);
      const boundingBox = new THREE.Box3().setFromObject(modelRef.current);
      const sizeVec = boundingBox.getSize(new THREE.Vector3());
      const maxDimension = Math.max(sizeVec.x, sizeVec.y, sizeVec.z);
      const minAllowedRadius = maxDimension * 0.02;

      let clusterRadius = centerDistance * clusterThreshold;
      clusterRadius = Math.max(clusterRadius, minAllowedRadius);

      console.log(
        `Center distance: ${centerDistance.toFixed(
          2
        )}, Cluster radius: ${clusterRadius.toFixed(2)}`
      );

      const mainClusterPoints = allPoints.filter(
        (point) => point.distanceTo(planeCenter) <= clusterRadius
      );

      console.log(
        `Total points: ${allPoints.length}, Main cluster: ${mainClusterPoints.length}`
      );

      let finalPoints = mainClusterPoints;
      if (maxRadius) {
        finalPoints = mainClusterPoints.filter(
          (point) => point.distanceTo(planeCenter) <= maxRadius
        );
        console.log(`After max radius filter: ${finalPoints.length} points`);
      }

      const transformedPoints = transformPointsToPlaneSpace(
        finalPoints,
        planeCenter,
        planeNormal
      );

      return transformedPoints;
    },
    [modelRef, shortestPath, sliceMeshWithPlane, transformPointsToPlaneSpace]
  );

  /*
  const getCrossSectionFromMesh = useCallback(
    (meshName, pointIndex, planeSize) => {
      // Find the target mesh by name.
      const targetMesh = modelRef.current.children.find(
        (child) => child.name === meshName
      );
      if (!targetMesh || !targetMesh.geometry) return [];

      // Compute default plane size based on the model's bounding box if not provided.
      const boundingBox = new THREE.Box3().setFromObject(modelRef.current);
      const sizeVec = boundingBox.getSize(new THREE.Vector3());
      const maxDimension = Math.max(sizeVec.x, sizeVec.y, sizeVec.z);
      const defaultPlaneSize = maxDimension * 0.2; // 0.2 of the max dimension
      planeSize = planeSize || defaultPlaneSize;

      // Compute the current visual plane parameters (center and normal) using the discrete index.
      const totalPoints = shortestPath.length;
      if (totalPoints < 2) return [];
      const clampedIndex = Math.max(0, Math.min(pointIndex, totalPoints - 1));
      const planeCenter = shortestPath[clampedIndex];

      let planeNormal;
      if (clampedIndex < totalPoints - 1) {
        const nextPoint = shortestPath[clampedIndex + 1];
        planeNormal = new THREE.Vector3()
          .subVectors(nextPoint, planeCenter)
          .normalize();
      } else {
        // If at the last point, use the previous point's direction if available.
        if (clampedIndex > 0) {
          const prevPoint = shortestPath[clampedIndex - 1];
          planeNormal = new THREE.Vector3()
            .subVectors(planeCenter, prevPoint)
            .normalize();
        } else {
          planeNormal = new THREE.Vector3(0, 0, 1);
        }
      }

      // Create a dummy plane (for intersection) using the computed center/normal.
      const plane = new THREE.Plane(planeNormal, -planeNormal.dot(planeCenter));

      // Slice the target mesh geometry with the plane.
      const segments = sliceMeshWithPlane(
        targetMesh.geometry,
        plane,
        targetMesh.matrixWorld
      );

      // Flatten the segments into a list of points.
      let points = [];
      segments.forEach((segment) => {
        points.push(segment[0], segment[1]);
      });

      // Filter: only keep points that are within half of planeSize from planeCenter.
      points = points.filter(
        (pt) => pt.distanceTo(planeCenter) <= planeSize / 2
      );

      // Transform the points into the plane's local (2D) coordinate system.
      const transformedPoints = transformPointsToPlaneSpace(
        points,
        planeCenter,
        planeNormal
      );

      return transformedPoints; // These are THREE.Vector3 objects with z ≃ 0.
    },
    [modelRef, shortestPath, sliceMeshWithPlane, transformPointsToPlaneSpace]
  );

  */
  const applySectionViewAtIndex = useCallback(
    (pointIndex, customMaxRadius = null, customClusterThreshold = 1.5) => {
      if (
        !modelRef.current ||
        !sceneRef?.current ||
        !shortestPath ||
        shortestPath.length < 2
      ) {
        return;
      }

      // Clamp the provided index between 0 and totalPoints - 1.
      const totalPoints = shortestPath.length;
      const clampedIndex = Math.max(0, Math.min(pointIndex, totalPoints - 1));

      // Use the point at clampedIndex as the plane center.
      const planeCenter = shortestPath[clampedIndex];

      // Determine the plane's normal.
      let planeNormal;
      if (clampedIndex < totalPoints - 1) {
        // If not at the last point, compute the normal towards the next point.
        const nextPoint = shortestPath[clampedIndex + 1];
        planeNormal = new THREE.Vector3()
          .subVectors(nextPoint, planeCenter)
          .normalize();
      } else {
        // If at the last point, use the previous point's direction if available.
        if (totalPoints > 1) {
          const prevPoint = shortestPath[clampedIndex - 1];
          planeNormal = new THREE.Vector3()
            .subVectors(planeCenter, prevPoint)
            .normalize();
        } else {
          planeNormal = new THREE.Vector3(0, 0, 1);
        }
      }

      // Create a dummy THREE.Plane using the computed normal and center.
      const dummyPlane = new THREE.Plane(
        planeNormal,
        -planeNormal.dot(planeCenter)
      );

      // Remove previous helper, if any.
      if (planeHelperRef.current) {
        sceneRef.current.remove(planeHelperRef.current);
        planeHelperRef.current.geometry.dispose();
        planeHelperRef.current.material.dispose();
        planeHelperRef.current = null;
      }

      // Determine helper size - daha küçük helper için
      let helperSize;
      if (customMaxRadius) {
        helperSize = customMaxRadius * 2;
      } else {
        const boundingBox = new THREE.Box3().setFromObject(modelRef.current);
        const sizeVec = boundingBox.getSize(new THREE.Vector3());
        const maxDimension = Math.max(sizeVec.x, sizeVec.y, sizeVec.z);
        helperSize = maxDimension * 0.15; // Daha küçük helper (0.2'den 0.1'e)
      }

      // Create and add the visible plane helper at planeCenter.
      const helper = createCustomPlaneHelper(
        dummyPlane,
        planeCenter,
        helperSize
      );
      sceneRef.current.add(helper);
      planeHelperRef.current = helper;

      // Clustering yöntemi ile cross-section hesapla
      const pts = getCrossSectionWithClustering(
        "AO", //TODO: modelRef.current.children[0].name,
        clampedIndex,
        customMaxRadius,
        customClusterThreshold
      );
      setCrossSectionPoints(pts);
    },
    [
      modelRef,
      sceneRef,
      shortestPath,
      getCrossSectionWithClustering,
      setCrossSectionPoints,
    ]
  );

  const removeSectionViewHelper = useCallback(() => {
    if (planeHelperRef.current && sceneRef.current) {
      sceneRef.current.remove(planeHelperRef.current);
      if (planeHelperRef.current.geometry) {
        planeHelperRef.current.geometry.dispose();
      }
      if (planeHelperRef.current.material) {
        planeHelperRef.current.material.dispose();
      }
      planeHelperRef.current = null;
    }
  }, [sceneRef]);

  return {
    extractCenterline,
    selectTwoPoints,
    shortestPath,
    currentIndex,
    setCurrentIndex,
    moveCameraToIndex,
    autoMoveCamera,
    stopAutoMoveCamera,
    pathSliderValue,
    setPathSliderValue,
    crossSectionPoints,
    applySectionViewAtIndex,
    removeSectionViewHelper,
    setIsCenterlineMode,
    isCenterlineMode,
    setIsCenterlineMeshSelectMode,
    isCenterlineMeshSelectMode,
  };
};
